/**
 * Wire up the "Be first to every drop" newsletter form to subscribe
 * to the Newsletter (welcome sequence) Klaviyo list directly via
 * Klaviyo's public client-side subscription API.
 *
 * Why: the form currently POSTs to Shopify's /contact endpoint which
 * doesn't reliably get the email into Klaviyo's biggest list. This
 * intercepts the submit, sends to Klaviyo first, then either lets
 * the Shopify form continue (for legacy capture) or replaces the UI
 * with a success state.
 *
 * Target Klaviyo list: PCjMSX — "Newsletter (welcome sequence)" — 453K profiles
 * Klaviyo public company ID: NBGdLy
 */
(function () {
  'use strict';

  var KLAVIYO_COMPANY_ID = 'NBGdLy';
  var KLAVIYO_LIST_ID = 'PCjMSX';

  // Klaviyo's client-side subscribe API. Documented at
  // https://developers.klaviyo.com/en/reference/create_client_subscription
  // Uses 2024-10-15 stable revision.
  var KLAVIYO_SUBSCRIBE_URL =
    'https://a.klaviyo.com/client/subscriptions/?company_id=' + KLAVIYO_COMPANY_ID;
  var KLAVIYO_API_REVISION = '2024-10-15';

  // The form selector — matches the "Be first to every drop" form on
  // the homepage (#email-signup) and the ambassador page.
  var FORM_SELECTORS = [
    '#email-signup form',
    '.ch26-signup form',
    '.coldest-ambassador-signup form',
    'form.coldest-newsletter-form',
  ];

  function subscribeToKlaviyo(email) {
    var payload = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'Homepage Hero — Be first to every drop',
          profile: {
            data: {
              type: 'profile',
              attributes: { email: email },
            },
          },
        },
        relationships: {
          list: { data: { type: 'list', id: KLAVIYO_LIST_ID } },
        },
      },
    };

    return fetch(KLAVIYO_SUBSCRIBE_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        revision: KLAVIYO_API_REVISION,
      },
      body: JSON.stringify(payload),
    }).then(function (r) {
      // 202 Accepted = success (queued). 200 = also success.
      if (r.status === 202 || r.status === 200) return { ok: true };
      return r.text().then(function (body) {
        return { ok: false, status: r.status, body: body.substring(0, 300) };
      });
    });
  }

  function isValidEmail(s) {
    return typeof s === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim());
  }

  function renderSuccess(form) {
    var container = form.parentElement;
    if (!container) return;
    // Show a clean success state in place of the form
    var msg = document.createElement('div');
    msg.className = 'coldest-newsletter-success';
    msg.setAttribute('role', 'status');
    msg.setAttribute('aria-live', 'polite');
    msg.style.cssText =
      'display:flex;align-items:center;justify-content:center;gap:12px;' +
      'padding:18px 24px;background:rgba(255,255,255,0.08);border-radius:999px;' +
      'border:1px solid rgba(255,255,255,0.15);color:#fff;font-weight:600;' +
      'max-width:560px;margin:0 auto;font-size:15px';
    msg.innerHTML =
      '<span aria-hidden="true" style="display:inline-flex;width:24px;height:24px;' +
      'background:#10B981;border-radius:50%;align-items:center;justify-content:center">' +
      '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>' +
      '</span>' +
      '<span>You\u2019re in. Watch your inbox for Thursday\u2019s drop.</span>';
    form.style.display = 'none';
    container.appendChild(msg);
  }

  function renderError(form, friendlyMsg) {
    // Find or create an inline error message
    var existing = form.querySelector('.coldest-newsletter-error');
    if (existing) existing.remove();
    var err = document.createElement('div');
    err.className = 'coldest-newsletter-error';
    err.setAttribute('role', 'alert');
    err.style.cssText =
      'margin-top:10px;padding:10px 14px;background:rgba(239, 68, 68, 0.15);' +
      'border:1px solid rgba(239, 68, 68, 0.4);color:#FCA5A5;border-radius:8px;' +
      'font-size:13px;text-align:center';
    err.textContent = friendlyMsg;
    form.appendChild(err);
    setTimeout(function () { err.remove(); }, 6000);
  }

  function handleSubmit(form, evt) {
    var emailInput = form.querySelector('input[type="email"], input[name="contact[email]"], input[name="email"]');
    if (!emailInput) return; // not our form
    var email = (emailInput.value || '').trim();
    if (!isValidEmail(email)) {
      // Let native validation handle it
      return;
    }

    // Stop the Shopify-bound submit
    evt.preventDefault();
    evt.stopImmediatePropagation();

    // Disable the submit button while we work
    var btn = form.querySelector('button[type="submit"], button:not([type="button"])');
    var origBtnText = '';
    if (btn) {
      origBtnText = btn.textContent;
      btn.disabled = true;
      btn.textContent = 'Subscribing…';
    }

    subscribeToKlaviyo(email)
      .then(function (res) {
        if (res.ok) {
          renderSuccess(form);
          // Dispatch a custom event so any other tracking can react
          try {
            document.dispatchEvent(new CustomEvent('coldest:newsletter:subscribed', {
              bubbles: true,
              detail: { email: email, list: KLAVIYO_LIST_ID }
            }));
          } catch (e) {}
        } else {
          // 400 from Klaviyo often means already-subscribed — still show success
          // so the user has a positive experience.
          if (res.status === 400 && (res.body || '').toLowerCase().indexOf('already') > -1) {
            renderSuccess(form);
            return;
          }
          // Other errors — fall back to letting the Shopify form submit so
          // the email still gets captured server-side.
          if (btn) {
            btn.disabled = false;
            btn.textContent = origBtnText;
          }
          if (window.console && console.warn) {
            console.warn('[coldest-newsletter] Klaviyo error, falling back to Shopify form', res);
          }
          // Re-submit, but this time let it through
          form.removeAttribute('data-cldst-newsletter-bound');
          form.submit();
        }
      })
      .catch(function (err) {
        if (btn) {
          btn.disabled = false;
          btn.textContent = origBtnText;
        }
        renderError(form, 'Something went wrong. Try again?');
        if (window.console && console.warn) {
          console.warn('[coldest-newsletter] Klaviyo fetch failed', err);
        }
      });
  }

  function bindForm(form) {
    if (form.hasAttribute('data-cldst-newsletter-bound')) return;
    form.setAttribute('data-cldst-newsletter-bound', '1');
    form.addEventListener('submit', function (evt) { handleSubmit(form, evt); }, true);
  }

  function bindAllForms() {
    FORM_SELECTORS.forEach(function (sel) {
      document.querySelectorAll(sel).forEach(bindForm);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindAllForms);
  } else {
    bindAllForms();
  }

  // Re-bind if forms get added dynamically (e.g. AJAX section renders)
  if ('MutationObserver' in window) {
    var obs = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].addedNodes.length > 0) {
          clearTimeout(obs._t);
          obs._t = setTimeout(bindAllForms, 250);
          break;
        }
      }
    });
    obs.observe(document.body, { childList: true, subtree: true });
  }
})();
